import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { AnimationElementTreetop } from '../animations/AnimationElementTreetop';
import { gsap } from 'gsap/all';
import PapandSVG from '@client/assets/svgs/papand.svg';
import ExclamationSvg from '@client/assets/svgs/exclamation.svg';

const StyledPapandSvg = styled(PapandSVG)`
	position: absolute;
	top: 0;
	right: 0;

	width: 150px;
	height: 150px;
	overflow: visible;
`;

const StyledExclamationSvg = styled(ExclamationSvg)`
	position: absolute;
	top: -80px;
	left: -40px;
	width: 70px;
	height: 70px;
	overflow: visible;
`;

export function PapandAnimation(props: { idleOnly: boolean }) {
	const idleOnlyRef = useRef<boolean>(props.idleOnly);
	const papandRef = useRef<SVGSVGElement>(null);
	const exclamationRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		idleOnlyRef.current = props.idleOnly;
	}, [props.idleOnly]);

	useEffect(() => {
		const query = gsap.utils.selector(papandRef.current);
		const exclamationQuery = gsap.utils.selector(exclamationRef.current);

		const quack = gsap.timeline({
			repeat: -1,
			repeatDelay: 0,
		});

		// Quack
		quack.fromTo(
			papandRef.current,
			{
				y: 0,
			},
			{
				y: -25,
				repeat: 1,
				yoyo: true,
				duration: 1,
				ease: 'elastic.inOut(1.2, 0.6)',
			},
			0
		);
		quack.fromTo(
			query('#beak-top'),
			{
				y: 0,
				x: 0,
				skewY: 0,
				skewX: 0,
				transformOrigin: 'center right',
			},
			{
				skewY: 25,
				skewX: 5,
				scaleX: 0.9,
				x: 7,
				scaleY: 0.4,
				y: -15,
				repeat: 1,
				yoyo: true,
				duration: 1,
				ease: 'elastic.inOut(1.2, 0.4)',
			},
			0
		);
		quack.fromTo(
			query('#beak-bottom'),
			{
				y: 0,
				x: 0,
				skewX: 0,
				scaleX: 1,
				transformOrigin: 'center right',
			},
			{
				scaleX: 0.9,
				skewX: 10,
				y: -18,
				x: 7,
				repeat: 1,
				yoyo: true,
				duration: 1,
				ease: 'elastic.inOut(1.2, 0.4)',
			},
			0
		);

		quack.fromTo(
			query('#eye'),
			{
				y: 0,
				x: 0,
				scale: 1,
				transformOrigin: 'center',
			},
			{
				scale: 1.2,
				x: 5,
				y: -3,
				repeat: 1,
				yoyo: true,
				duration: 1,
				ease: 'elastic.inOut(1, 1)',
			},
			0
		);
		quack.fromTo(
			query('#iris'),
			{
				y: 0,
				x: 0,
				scale: 1,
				transformOrigin: 'center',
			},
			{
				scale: 0.6,
				x: -8,
				y: 2,
				repeat: 1,
				yoyo: true,
				duration: 1,
				ease: 'elastic.inOut(1, 0.8)',
			},
			0
		);

		quack.fromTo(
			query('#wing'),
			{
				y: 0,
			},
			{
				y: -9,
				yoyo: true,
				repeat: 1,
				duration: 1,
				ease: 'elastic.inOut(1.2, 0.5)',
			},
			0
		);
		quack.fromTo(
			query('#wing'),
			{
				rotate: 0,
				skewX: 0,
				skewY: 0,
				scaleX: 1,
				x: 0,
				transformOrigin: 'left',
			},
			{
				rotate: -5,
				skewX: 30,
				skewY: 5,
				scaleX: 1.2,
				x: 14,
				repeat: 3,
				yoyo: true,
				delay: 0.5,
				duration: 0.25,
				ease: 'back.out(1.3)',
			},
			0
		);

		quack.fromTo(
			query('#body'),
			{
				skewY: 0,
				skewX: 0,
				scaleY: 1,
				scaleX: 1,
				transformOrigin: 'bottom',
			},
			{
				skewY: -2,
				skewX: -2,
				scaleY: 1.05,
				scaleX: 0.95,
				yoyo: true,
				repeat: 1,
				duration: 1,
				ease: 'elastic.inOut(1.2, 0.5)',
			},
			0
		);

		// Exlamation
		quack.fromTo(
			[exclamationQuery('#exclamation-1'), exclamationQuery('#exclamation-2')],
			{
				y: 4,
				scale: 0.5,
				opacity: 0,
				transformOrigin: 'bottom',
			},
			{
				scale: 1,
				y: 0,
				x: 0,
				opacity: 1,
				stagger: 0.1,
				repeat: 1,
				yoyo: true,
				delay: 0.6,
				duration: 0.2,
				repeatDelay: 0.5,
				ease: 'elastic.out(1.2, 0.9)',
			},
			0
		);
		quack.fromTo(
			exclamationQuery('#cloud'),
			{
				rotate: -5,
				scale: 0.8,
				opacity: 0,
				transformOrigin: 'center',
			},
			{
				opacity: 1,
				rotate: 0,
				scale: 1,
				repeat: 1,
				yoyo: true,
				delay: 0.5,
				duration: 0.2,
				repeatDelay: 0.8,
				ease: 'elastic.out(1.2, 0.9)',
			},
			0
		);
		quack.fromTo(
			[exclamationQuery('#circle-1'), exclamationQuery('#circle-2')],
			{
				scale: 0,
				transformOrigin: 'center',
			},
			{
				scale: 1,
				repeat: 1,
				yoyo: true,
				stagger: 0.2,
				delay: 0,
				duration: 0.4,
				repeatDelay: 0.3,
				ease: 'elastic.out(2, 0.9)',
			},
			0
		);

		quack.addLabel('idle-begin');

		// Eye idle
		quack.fromTo(
			query('#iris'),
			{
				y: 0,
				x: 0,
				transformOrigin: 'center',
			},
			{
				keyframes: {
					x: [0, -7, -6, -8, 0],
					y: [0, 2, -1, 3, 0],
					easeEach: 'elastic.inOut(1.1,1)',
				},
				duration: 4,
			},
			'idle-begin'
		);

		quack.fromTo(
			papandRef.current,
			{
				skewY: 0,
				skewX: 0,
				scaleY: 1,
				scaleX: 1,
				transformOrigin: 'bottom',
			},
			{
				keyframes: {
					scaleX: [1, 1, 1.025, 0.98],
					scaleY: [1, 1, 0.98, 1.025],
					// easeEach: 'elastic.inOut(1.1,1)',
					easeEach: 'power3.out',
				},
				repeat: 7,
				yoyo: true,
				duration: 0.5,
				// ease: 'sine.inOut',
			},
			'idle-begin'
		);

		quack.call(() => {
			if (idleOnlyRef.current) {
				quack.play('idle-begin');
			}
		});

		return () => {
			quack.kill();
		};
	}, []);

	return (
		<>
			<AnimationElementTreetop
				variant={3}
				position={{
					breakpoints: {
						tiny: {
							width: '300px',
						},
						mobile: {
							width: '300px',
						},
						desktop: {
							width: '300px',
						},
						largeDesktop: {
							width: '300px',
						},
						ultra: {
							width: '300px',
						},
					},
				}}
			/>

			<StyledExclamationSvg ref={exclamationRef} />
			<StyledPapandSvg ref={papandRef} />

			<AnimationElementTreetop
				variant={2}
				position={{
					y: '30%',
					flip: true,
					breakpoints: {
						tiny: {
							width: '300px',
						},
						mobile: {
							width: '300px',
						},
						desktop: {
							width: '300px',
						},
						largeDesktop: {
							width: '300px',
						},
						ultra: {
							width: '300px',
						},
					},
				}}
			/>
		</>
	);
}
